import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';

export interface SmartLinkPageIds {
  accountId: number;
  smartLinkId: number;
}

/** A positive whole number written only with digits ("12"), else null. "abc", "-1", "1.5" give null. */
function positiveIdOf(value: string | undefined | null): number | null {
  if (value === undefined || value === null || !/^\d{1,15}$/.test(value.trim())) {
    return null;
  }
  const id = Number(value.trim());
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * The ids of the SmartLink manage and editor pages (`?accountId=&smartLinkId=`), or null when one is
 * missing or is not a positive whole number (QA finding brk-2: `accountId=abc` crashed the app).
 * The page then shows the "not available" state and sends no request. Pure.
 */
export function smartLinkPageIdsOf(
  accountId: string | undefined | null,
  smartLinkId: string | undefined | null
): SmartLinkPageIds | null {
  const account = positiveIdOf(accountId);
  const smartLink = positiveIdOf(smartLinkId);
  if (account === null || smartLink === null) {
    return null;
  }
  return { accountId: account, smartLinkId: smartLink };
}

/** The answers that mean "this page is not yours or not there": 400, 401, 403 and 404. */
const UNAVAILABLE_STATUSES = new Set([400, 401, 403, 404]);

/**
 * True when the read of a SmartLink failed because the page is not in this workspace or does not
 * exist. The page then shows one clean state, never the raw server text ("Not authenticated", "The
 * client does not have access rights to the content"). A 5xx or a network error stays an error
 * with "Try again". Pure.
 */
export function isSmartLinkUnavailableError(error: unknown): boolean {
  return error instanceof FYMAPIError && UNAVAILABLE_STATUSES.has(error.statusCode);
}
